import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface DocumentRow {
  readonly documentId: string;
  readonly documentCode: string;
  readonly documentName: string;
  readonly documentType: string | null;
  readonly classification: string;
  readonly status: string;
  readonly currentVersion: string;
  readonly archiveTier: string;
}

const STATUSES = ['', 'DRAFT', 'SUBMITTED', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'PUBLISHED', 'ARCHIVED'];
const CLASSIFICATIONS = ['', 'PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED', 'HIGHLY_RESTRICTED'];

interface SignedUrl {
  readonly url?: string;
  readonly signedUrl?: string;
}

export function M23RepositoryBrowserPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [status, setStatus] = useState('');
  const [classification, setClassification] = useState('');
  const [rows, setRows] = useState<DocumentRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function load(): void {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (classification) params.set('classification', classification);
    const qs = params.toString();
    apiRequest<{ data: DocumentRow[]; total: number }>(`/v1/documents${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { setRows(r.data); setTotal(r.total); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  async function onDownload(id: string): Promise<void> {
    setError(null);
    setNotice(null);
    setBusy(`download:${id}`);
    try {
      const r = await apiRequest<{ data: SignedUrl }>(`/v1/documents/${encodeURIComponent(id)}/signed-url`, { token });
      const url = r.data.signedUrl ?? r.data.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        setNotice(t('m23.repository.downloadOpened'));
      } else {
        setNotice(t('m23.repository.signedUrlIssued'));
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function onCheckout(id: string): Promise<void> {
    setError(null);
    setNotice(null);
    setBusy(`checkout:${id}`);
    try {
      await apiRequest(`/v1/documents/${encodeURIComponent(id)}/checkout`, { method: 'POST', token });
      setNotice(t('m23.repository.checkedOut'));
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function onDelete(id: string): Promise<void> {
    if (!window.confirm(t('m23.repository.deleteConfirm'))) return;
    setError(null);
    setNotice(null);
    setBusy(`delete:${id}`);
    try {
      await apiRequest(`/v1/documents/${encodeURIComponent(id)}`, { method: 'DELETE', token });
      setNotice(t('m23.repository.deleted'));
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <main aria-label="repository-browser">
      <h1>{t('m23.repository.title')}</h1>
      <form aria-label="repository-filter-form" onSubmit={(e) => { e.preventDefault(); load(); }}>
        <label htmlFor="status">{t('m23.repository.status')}</label>
        <select id="status" value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUSES.map((s) => <option key={s} value={s}>{s || t('m23.repository.all')}</option>)}
        </select>
        <label htmlFor="classification">{t('m23.repository.classification')}</label>
        <select id="classification" value={classification} onChange={(e) => setClassification(e.target.value)}>
          {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c || t('m23.repository.all')}</option>)}
        </select>
        <button type="submit">{t('m23.repository.applyFilters')}</button>
      </form>
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      {loading && <p>{t('m23.repository.loading')}</p>}
      {rows && (
        <>
          <p aria-label="result-count">{t('m23.repository.resultCount', { count: total })}</p>
          <table aria-label="documents-table">
            <thead><tr><th>{t('m23.repository.colCode')}</th><th>{t('m23.repository.colName')}</th><th>{t('m23.repository.colType')}</th><th>{t('m23.repository.colClassification')}</th><th>{t('m23.repository.colStatus')}</th><th>{t('m23.repository.colVersion')}</th><th>{t('m23.repository.colTier')}</th><th>{t('m23.repository.colActions')}</th></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.documentId}>
                  <td>{d.documentCode}</td>
                  <td>{d.documentName}</td>
                  <td>{d.documentType ?? '—'}</td>
                  <td>{d.classification}</td>
                  <td>{d.status}</td>
                  <td>{d.currentVersion}</td>
                  <td>{d.archiveTier}</td>
                  <td>
                    <button type="button" aria-label={`download-${d.documentId}`} disabled={busy !== null} onClick={() => onDownload(d.documentId)}>{t('m23.repository.download')}</button>
                    <button type="button" aria-label={`checkout-${d.documentId}`} disabled={busy !== null} onClick={() => onCheckout(d.documentId)}>{t('m23.repository.checkout')}</button>
                    <button type="button" aria-label={`delete-${d.documentId}`} disabled={busy !== null} onClick={() => onDelete(d.documentId)}>{t('m23.repository.delete')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
